/**
 * Phase 26 — PlaceVideoLightbox (web).
 *
 * Opened by the tiny superscript ▶ next to a city/place name. A
 * half-screen panel (bottom sheet on mobile widths, centered panel on
 * desktop — never full screen; YouTube's own fullscreen button still
 * works inside the iframe) that:
 *   1. asks the sponsors backend for a matching pre-roll video ad
 *      (7/14 s direct-sold creative; worldwide unless targeted). If
 *      none — skip straight to the video, never block on ads.
 *   2. then embeds YouTube's KEYLESS search-playlist player
 *      (listType=search) for "<place> travel vlog", which plays the
 *      top-ranked creator video for the place — no YouTube API key,
 *      no quota, no cost.
 */
import { useEffect, useRef, useState } from "react";
import { X } from "lucide-react";

const BACKEND = process.env.REACT_APP_BACKEND_URL || "";

export default function PlaceVideoLightbox({ place, onClose }) {
    const [phase, setPhase] = useState("loading"); // loading | ad | video
    const [ad, setAd] = useState(null);
    const [adLeft, setAdLeft] = useState(0);
    const videoRef = useRef(null);
    const timerRef = useRef(null);

    // 1. Fetch a pre-roll (never block: any failure → straight to video).
    useEffect(() => {
        let gone = false;
        (async () => {
            try {
                const res = await fetch(
                    `${BACKEND}/api/sponsors/video-ad?destination=${encodeURIComponent(place || "")}`,
                );
                const data = await res.json();
                if (gone) return;
                if (data?.ad?.video_url) {
                    setAd(data.ad);
                    setAdLeft(data.ad.duration_s || 7);
                    setPhase("ad");
                } else {
                    setPhase("video");
                }
            } catch {
                if (!gone) setPhase("video");
            }
        })();
        return () => { gone = true; };
    }, [place]);

    // 2. Ad countdown + hard stop at duration_s (creative may be longer).
    useEffect(() => {
        if (phase !== "ad" || !ad) return undefined;
        timerRef.current = setInterval(() => {
            setAdLeft((s) => {
                if (s <= 1) {
                    clearInterval(timerRef.current);
                    fetch(`${BACKEND}/api/sponsors/video-ad/${ad.id}/complete`, { method: "POST" }).catch(() => {});
                    setPhase("video");
                    return 0;
                }
                return s - 1;
            });
        }, 1000);
        return () => clearInterval(timerRef.current);
    }, [phase, ad]);

    useEffect(() => {
        const onKey = (e) => e.key === "Escape" && onClose?.();
        window.addEventListener("keydown", onKey);
        return () => window.removeEventListener("keydown", onKey);
    }, [onClose]);

    const query = `${String(place || "").trim()} travel vlog`;
    const ytSrc =
        "https://www.youtube-nocookie.com/embed?listType=search&list=" +
        encodeURIComponent(query) +
        "&autoplay=1&rel=0";

    return (
        <div className="fixed inset-0 z-[90] flex items-end justify-center bg-black/50 sm:items-center"
             data-testid="place-video-lightbox"
             onClick={(e) => { if (e.target === e.currentTarget) onClose?.(); }}>
            <div className="flex h-[50vh] w-full flex-col overflow-hidden rounded-t-2xl bg-black sm:h-[50vh] sm:max-w-2xl sm:rounded-2xl">
                <div className="flex items-center justify-between bg-[#111] px-3 py-2">
                    <span className="truncate text-[12px] font-semibold text-white">
                        ▶ {place}
                        {phase === "ad" && ad && (
                            <span className="ml-2 font-normal text-[#A3A3A3]"
                                  data-testid="place-video-ad-label">
                                Sponsored · {ad.sponsor} · video in {adLeft}s
                            </span>
                        )}
                    </span>
                    <button type="button" onClick={onClose} aria-label="Close video"
                            data-testid="place-video-close"
                            className="rounded p-1 text-white/80 transition hover:bg-white/10">
                        <X size={16} />
                    </button>
                </div>
                <div className="relative flex-1">
                    {phase === "loading" && (
                        <div className="flex h-full items-center justify-center text-[12px] text-[#A3A3A3]">
                            Loading…
                        </div>
                    )}
                    {phase === "ad" && ad && (
                        <video
                            ref={videoRef}
                            src={ad.video_url}
                            autoPlay
                            playsInline
                            data-testid="place-video-ad"
                            onEnded={() => {
                                clearInterval(timerRef.current);
                                fetch(`${BACKEND}/api/sponsors/video-ad/${ad.id}/complete`, { method: "POST" }).catch(() => {});
                                setPhase("video");
                            }}
                            onError={() => setPhase("video")}
                            onClick={() => ad.click_url && window.open(ad.click_url, "_blank", "noopener")}
                            className="h-full w-full cursor-pointer object-contain"
                        />
                    )}
                    {phase === "video" && (
                        <iframe
                            title={`Videos: ${place}`}
                            src={ytSrc}
                            data-testid="place-video-youtube"
                            className="h-full w-full"
                            frameBorder="0"
                            allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                            allowFullScreen
                        />
                    )}
                </div>
            </div>
        </div>
    );
}
